export function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

export function setCookie(name: string, value: string, maxAgeSeconds: number): void {
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; SameSite=Lax; max-age=${maxAgeSeconds}${secure}`;
}

export function deleteCookie(name: string): void {
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax${secure}`;
}

// CSRF mirror cookies are per-API-host. Each microapp API issues its own
// csrf_token cookie on its own host; the SPA can't read those cross-origin
// cookies, so the login/refresh response body echoes the value and the SPA
// writes a mirror on its own host. With multiple API hosts in play (microapp
// shell that talks to N microapp APIs), a single generic mirror name would
// be overwritten by each successive login and the next request to a
// different API would carry the wrong token — CsrfMiddleware then rejects
// with 403. Suffix the cookie name with the API host so each mirror is
// independent and the request interceptor can pick the right one per URL.
const CSRF_COOKIE_PREFIX: string = "csrf_token__";

function getApiHostKey(url: string | undefined): string {
  if (!url) return "";
  try {
    return new URL(url, window.location.origin).host;
  } catch {
    return "";
  }
}

export function readCsrfMirrorForUrl(url: string | undefined): string | null {
  const host: string = getApiHostKey(url);
  if (host) {
    const scoped: string | null = getCookie(`${CSRF_COOKIE_PREFIX}${host}`);
    if (scoped) return scoped;
  }
  // Pre-fix sessions still carry the legacy bare csrf_token cookie. Read
  // it as a fallback so an active session isn't stranded after deploy;
  // the next login/refresh writes a properly host-scoped mirror and the
  // bare cookie phases out naturally (deleteAllCsrfMirrors clears it).
  return getCookie("csrf_token");
}

export function writeCsrfMirrorForUrl(url: string | undefined, value: string, maxAgeSeconds: number): void {
  const host: string = getApiHostKey(url);
  if (!host) return;
  setCookie(`${CSRF_COOKIE_PREFIX}${host}`, value, maxAgeSeconds);
}

export function deleteAllCsrfMirrors(): void {
  // Legacy bare cookie from pre-fix versions.
  deleteCookie("csrf_token");
  // Every host-scoped mirror currently visible to document.cookie. Iterating
  // here is fine — generated apps talk to a small handful of API hosts.
  const names: string[] = document.cookie.split(";").map((s: string): string => s.trim().split("=")[0]);
  for (const name of names) {
    if (name.startsWith(CSRF_COOKIE_PREFIX)) {
      deleteCookie(name);
    }
  }
}
